import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { CheckCircle2, Plus, ShieldAlert, Star } from 'lucide-react';
import { api } from '@/lib/api';
import { useConfirm } from '@/components/ui/confirm';
import { usePermission } from '@/Hooks/usePermission';
import { formatDateTime } from '@/lib/i18n';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Field, Textarea } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { apiError, type AddStorefrontDomainInput, type StorefrontDomain } from './storefrontLib';

/**
 * Domains — the screen `verifiedAt` is FOR. A claim exists the moment it is
 * added; it is a boundary, and starts actually serving the storefront, only
 * once verified. Every unverified row says so in the operator's own words,
 * with the exact TXT record to publish, rather than a dead site an operator
 * has to guess about.
 */
export default function DomainsTab() {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const confirmDialog = useConfirm();
  const canManage = can('storefront.manage_domains');

  const [rows, setRows] = useState<StorefrontDomain[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [addOpen, setAddOpen] = useState(false);
  const [hostname, setHostname] = useState('');
  const [saving, setSaving] = useState(false);

  const [verifying, setVerifying] = useState<StorefrontDomain | null>(null);
  const [txtInput, setTxtInput] = useState('');
  const [verifySaving, setVerifySaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<StorefrontDomain[]>('/storefront/admin/domains')
      .then(({ data }) => setRows(data ?? []))
      .catch((e) => setError(apiError(e, t('storefront.domains.loadFailed'))))
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(load, [load]);

  const add = async () => {
    if (!hostname.trim()) return;
    setSaving(true);
    try {
      const payload: AddStorefrontDomainInput = { hostname: hostname.trim() };
      await api.post('/storefront/admin/domains', payload);
      toast.success(t('storefront.domains.added'));
      setAddOpen(false);
      setHostname('');
      load();
    } catch (e) {
      toast.error(apiError(e, t('storefront.domains.addFailed')));
    } finally {
      setSaving(false);
    }
  };

  const verify = async () => {
    if (!verifying) return;
    const txtValues = txtInput
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);
    if (txtValues.length === 0) return;
    setVerifySaving(true);
    try {
      await api.post(`/storefront/admin/domains/${verifying.id}/verify`, { txtValues });
      toast.success(t('storefront.domains.verified'));
      setVerifying(null);
      setTxtInput('');
      load();
    } catch (e) {
      toast.error(apiError(e, t('storefront.domains.verifyFailed')));
    } finally {
      setVerifySaving(false);
    }
  };

  const setPrimary = async (domain: StorefrontDomain) => {
    try {
      await api.post(`/storefront/admin/domains/${domain.id}/primary`);
      toast.success(t('storefront.domains.primarySet'));
      load();
    } catch (e) {
      toast.error(apiError(e, t('storefront.domains.primaryFailed')));
    }
  };

  const remove = async (domain: StorefrontDomain) => {
    const ok = await confirmDialog.confirm({
      title: t('storefront.domains.removeConfirmTitle', { hostname: domain.hostname }),
      body: t('storefront.domains.removeConfirmBody'),
      confirmLabel: t('common.delete'),
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await api.delete(`/storefront/admin/domains/${domain.id}`);
      toast.success(t('storefront.domains.removed'));
      load();
    } catch (e) {
      toast.error(apiError(e, t('storefront.domains.removeFailed')));
    }
  };

  return (
    <div className="space-y-3">
      {canManage && (
        <div className="flex justify-end">
          <Button size="sm" onClick={() => setAddOpen(true)}>
            <Plus className="h-4 w-4" />
            {t('storefront.domains.add')}
          </Button>
        </div>
      )}

      {loading ? (
        <LoadingState label={t('common.loading')} />
      ) : error ? (
        <ErrorState message={error} onRetry={load} />
      ) : rows.length === 0 ? (
        <EmptyState
          title={t('storefront.domains.empty')}
          hint={t('storefront.domains.emptyHint')}
          action={
            canManage ? (
              <Button size="sm" onClick={() => setAddOpen(true)}>
                {t('storefront.domains.add')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="grid gap-2 sm:grid-cols-2">
          {rows.map((domain) => (
            <Card key={domain.id}>
              <CardContent className="space-y-2 p-4">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-medium">{domain.hostname}</p>
                  {domain.isPrimary && (
                    <Badge tone="brand">
                      <Star className="me-1 h-3 w-3" />
                      {t('storefront.domains.primary')}
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-ink-muted">
                  {t(`storefront.domainKind.${domain.kind}`)}
                </p>

                {domain.verifiedAt ? (
                  <div className="flex items-center gap-1.5 text-sm text-green-700">
                    <CheckCircle2 className="h-4 w-4" />
                    {t('storefront.domains.verifiedAt', {
                      date: formatDateTime(domain.verifiedAt, i18n.language),
                    })}
                  </div>
                ) : (
                  <div className="flex items-start gap-1.5 rounded-lg border border-amber-300 bg-amber-50 p-2 text-sm text-amber-800">
                    <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>{t('storefront.domains.notVerified')}</span>
                  </div>
                )}

                {canManage && (
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {!domain.verifiedAt && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setVerifying(domain);
                          setTxtInput('');
                        }}
                      >
                        {t('storefront.domains.verify')}
                      </Button>
                    )}
                    {domain.verifiedAt && !domain.isPrimary && (
                      <Button variant="outline" size="sm" onClick={() => void setPrimary(domain)}>
                        {t('storefront.domains.makePrimary')}
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => void remove(domain)}>
                      {t('storefront.domains.release')}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        title={t('storefront.domains.add')}
        description={t('storefront.domains.addDescription')}
        className="max-w-md"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setAddOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button disabled={saving || !hostname.trim()} onClick={() => void add()}>
              {t('storefront.domains.add')}
            </Button>
          </div>
        }
      >
        <Field label={t('storefront.domains.hostname')} hint="order.example.com">
          <Input
            value={hostname}
            placeholder="order.example.com"
            onChange={(e) => setHostname(e.target.value)}
          />
        </Field>
      </Dialog>

      <Dialog
        open={Boolean(verifying)}
        onClose={() => setVerifying(null)}
        title={t('storefront.domains.verifyTitle')}
        className="max-w-lg"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setVerifying(null)}>
              {t('common.cancel')}
            </Button>
            <Button disabled={verifySaving || !txtInput.trim()} onClick={() => void verify()}>
              {t('storefront.domains.verify')}
            </Button>
          </div>
        }
      >
        {verifying && (
          <div className="space-y-3 text-sm">
            <p>{t('storefront.domains.verifyInstructions')}</p>
            <div className="space-y-1 rounded-lg border bg-subtle p-3 font-mono text-xs">
              <p>
                <span className="text-ink-muted">{t('storefront.domains.recordName')}: </span>
                {verifying.verificationRecord.name}
              </p>
              <p>
                <span className="text-ink-muted">{t('storefront.domains.recordType')}: </span>
                {verifying.verificationRecord.type}
              </p>
              <p>
                <span className="text-ink-muted">{t('storefront.domains.recordValue')}: </span>
                {verifying.verificationRecord.value}
              </p>
            </div>
            <Field
              label={t('storefront.domains.observedTxt')}
              hint={t('storefront.domains.observedTxtHint')}
            >
              <Textarea rows={3} value={txtInput} onChange={(e) => setTxtInput(e.target.value)} />
            </Field>
          </div>
        )}
      </Dialog>
    </div>
  );
}
